import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getInvoiceStatusBadge } from "@/lib/invoice-status";
import { RotateCcw, Trash2, Undo2 } from "lucide-react";

interface InvoiceNavigationCardProps {
  currentPosition: number;
  totalCount: number;
  onSkip?: () => void;
  onApprove?: () => void;
  onPrevious?: () => void;
  onReprocess?: () => void;
  onReturnToReview?: () => void;
  onDelete?: () => void;
  invoiceStatus?: string;
  disabled?: boolean;
}

export const InvoiceNavigationCard = ({
  currentPosition,
  totalCount,
  onSkip,
  onApprove,
  onPrevious,
  onReprocess,
  onReturnToReview,
  onDelete,
  invoiceStatus,
  disabled = false,
}: InvoiceNavigationCardProps): JSX.Element => {
  const safePosition = totalCount > 0 ? Math.min(Math.max(currentPosition, 1), totalCount) : 0;
  const isSingleInvoice = totalCount <= 1;

  const badgeConfig = getInvoiceStatusBadge(invoiceStatus);

  return (
    <Card className="flex items-center gap-4 self-stretch w-full flex-[0_0_auto] bg-neutral-100 rounded-xl">
      <CardContent className="flex items-center gap-4 px-6 py-6 flex-1 grow">
        <div className="flex items-center gap-3">
          <span className="font-text-sm-medium text-neutral-950">
            {totalCount > 0 ? `${safePosition}/${totalCount}` : '0/0'}
          </span>
          <Badge
            className={cn(
              "h-[22px] px-2.5 py-0.5 rounded-lg border border-solid",
              badgeConfig.bgColor,
              badgeConfig.borderColor,
              `hover:${badgeConfig.bgColor}/80 active:${badgeConfig.bgColor}/70`
            )}
          >
            <div className={cn("font-text-xs-medium", badgeConfig.textColor)}>
              {badgeConfig.label}
            </div>
          </Badge>
          {onReprocess && invoiceStatus !== 'processing' && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-neutral-500 hover:text-amber-600"
                    onClick={onReprocess}
                    disabled={disabled}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Reprocess invoice</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {onReturnToReview && (invoiceStatus === 'exported' || invoiceStatus === 'reviewed') && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-neutral-500 hover:text-blue-600"
                    onClick={onReturnToReview}
                    disabled={disabled}
                  >
                    <Undo2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Return to review</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {onDelete && invoiceStatus !== 'processing' && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-neutral-500 hover:text-red-600"
                    onClick={onDelete}
                    disabled={disabled}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete invoice</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        <div className="flex-1 flex justify-center">
          <span className="font-text-sm-regular text-neutral-500">Changes are saved</span>
        </div>

        <div className="flex items-center gap-2">
          {isSingleInvoice ? (
            <>
              <Button
                variant="outline"
                className="bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                onClick={onPrevious}
                disabled={disabled}
              >
                Cancel
              </Button>
              <Button
                className="bg-[#0000ff] text-white shadow-box-shadow-shadow-xs h-9 px-4 py-2 rounded-lg hover:bg-[#3333ff]"
                onClick={onApprove}
                disabled={disabled}
              >
                Approve
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlineAccent"
                className="bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                onClick={onPrevious}
                disabled={disabled}
              >
                Previous
              </Button>
              <Button
                variant="outlineAccent"
                className="bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                onClick={onSkip}
                disabled={disabled}
              >
                Skip
              </Button>
              <Button
                className="bg-[#0000ff] text-white shadow-box-shadow-shadow-xs h-9 px-4 py-2 rounded-lg hover:bg-[#3333ff]"
                onClick={onApprove}
                disabled={disabled}
              >
                Approve & Next
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
